(() => {
  'use strict';

  const root = document.querySelector('#MainContent[data-template*="raitooe-support-v3"]');
  if (!root || root.dataset.sup3Ready === 'true') return;
  root.dataset.sup3Ready = 'true';

  const contactFormKey = 'sup3-contact-form-id';
  const returnTargets = new WeakMap();

  const resolveDialog = (trigger) =>
    trigger.closest('.support-v3')?.querySelector('[data-sup3-dialog]') ||
    root.querySelector('[data-sup3-dialog="primary"]') ||
    root.querySelector('[data-sup3-dialog]');

  const refreshTriggers = () => {
    root.querySelectorAll('[data-sup3-open]').forEach((trigger) => {
      const dialog = resolveDialog(trigger);
      trigger.disabled = !dialog;
      trigger.setAttribute('aria-haspopup', 'dialog');
      if (dialog?.id) trigger.setAttribute('aria-controls', dialog.id);
      else trigger.removeAttribute('aria-controls');
    });
    root.querySelectorAll('[data-sup3-stage]').forEach((tab) => {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
    });
  };

  const consultationContext = (trigger) => {
    const section = trigger.closest('section');
    const selected = section?.querySelector('[data-sup3-mood][aria-pressed="true"], [data-sup3-stage][aria-selected="true"]');
    return [section?.querySelector('h1,h2')?.textContent, selected?.textContent, selected?.dataset.title]
      .map((value) => value?.replace(/\s+/g, ' ').trim()).filter(Boolean).join(' / ');
  };

  const openDialog = (dialog, trigger) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    returnTargets.set(dialog, trigger instanceof HTMLElement ? trigger : document.activeElement);
    if (trigger) {
      const context = dialog.querySelector('[data-sup3-context]');
      if (context) context.value = consultationContext(trigger);
    }
    if (!dialog.open) dialog.showModal();
    (dialog.querySelector('form select, form input:not([type="hidden"]), form textarea') || dialog.querySelector('[data-sup3-close]'))?.focus();
  };

  const closeDialog = (dialog) => {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.close();
  };

  root.addEventListener('close', (event) => {
    if (!event.target.matches?.('[data-sup3-dialog]')) return;
    const trigger = returnTargets.get(event.target);
    if (trigger?.isConnected) trigger.focus();
    returnTargets.delete(event.target);
  }, true);

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const mood = target.closest('[data-sup3-mood]');
    if (mood) {
      const section = mood.closest('.sup3-moods');
      section?.querySelectorAll('[data-sup3-mood]').forEach((button) => {
        const active = button === mood;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const label = section?.querySelector('[data-sup3-mood-label]');
      const title = section?.querySelector('[data-sup3-mood-title]');
      const copy = section?.querySelector('[data-sup3-mood-copy]');
      if (label) label.textContent = mood.dataset.label || '';
      if (title) title.textContent = mood.dataset.title || '';
      if (copy) copy.textContent = mood.dataset.copy || '';
      section?.querySelectorAll('[data-sup3-mood-resources]').forEach((links) => {
        links.hidden = links.dataset.sup3MoodResources !== mood.dataset.sup3Mood;
      });
      return;
    }

    const stage = target.closest('[data-sup3-stage]');
    if (stage) {
      const section = stage.closest('.sup3-lifecycle');
      section?.querySelectorAll('[data-sup3-stage]').forEach((button) => {
        const active = button === stage;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      const values = {
        number: section?.querySelector('[data-sup3-life-number]'),
        label: section?.querySelector('[data-sup3-life-label]'),
        title: section?.querySelector('[data-sup3-life-title]'),
        copy: section?.querySelector('[data-sup3-life-copy]')
      };
      if (values.number) values.number.textContent = stage.dataset.number || '';
      if (values.label) values.label.textContent = stage.dataset.label || '';
      if (values.title) values.title.textContent = stage.dataset.title || '';
      if (values.copy) values.copy.textContent = stage.dataset.copy || '';
      const products = section?.querySelector('[data-sup3-products]');
      if (products) products.hidden = stage.dataset.productLinks !== 'true';
      const activation = section?.querySelector('[data-sup3-activation]');
      if (activation) activation.hidden = stage.dataset.activation !== 'true';
      return;
    }

    const openButton = target.closest('[data-sup3-open]');
    if (openButton) {
      const dialog = resolveDialog(openButton);
      openDialog(dialog, openButton);
      return;
    }

    const closeButton = target.closest('[data-sup3-close]');
    if (closeButton) {
      closeDialog(closeButton.closest('[data-sup3-dialog]'));
      return;
    }

    if (target.matches('[data-sup3-dialog]')) {
      const bounds = target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog(target);
    }
  });

  root.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[data-sup3-stage]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...tab.closest('.sup3-lifecycle').querySelectorAll('[data-sup3-stage]')];
    let index = tabs.indexOf(tab);
    if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = tabs.length - 1;
    tabs[index].click();
    tabs[index].focus();
  });

  root.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (event.defaultPrevented || !form?.closest('[data-sup3-dialog]') || !form.action.includes('/contact')) return;

    try {
      sessionStorage.setItem(contactFormKey, form.id);
    } catch (error) {}
  });

  refreshTriggers();
  document.addEventListener('shopify:section:load', refreshTriggers);
  document.addEventListener('shopify:section:reorder', refreshTriggers);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refreshTriggers));

  let submittedFormId = '';
  try {
    submittedFormId = sessionStorage.getItem(contactFormKey) || '';
    sessionStorage.removeItem(contactFormKey);
  } catch (error) {}

  let hashFormId = '';
  try { hashFormId = decodeURIComponent(window.location.hash.slice(1)); } catch (error) {}
  const dialogs = [...root.querySelectorAll('[data-sup3-dialog]')];
  const findFormDialog = (id) => dialogs.find((dialog) => dialog.querySelector('form')?.id === id);
  const submittedDialog = findFormDialog(hashFormId) || findFormDialog(submittedFormId);
  const fallbackDialog = dialogs.find((dialog) => dialog.querySelector('[data-sup3-auto-open]'));

  if (submittedDialog?.querySelector('[data-sup3-auto-open]')) {
    openDialog(submittedDialog, null);
  } else if (!hashFormId && !submittedFormId && fallbackDialog) {
    openDialog(fallbackDialog, null);
  }
})();
